import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { MasterService } from '../base-classes/master-service';

export interface ExecutionResult {
  id?: string;
  packageId?: string;
  functionalAreaId?: string;
  scenarioId?: string;
  stepId?: string;
  testDescription?: string;
  statusId?: string;
  status?: string;
  statusText?: string;
  executedBy?: string;
  startedAt?: Date;
  endedAt?: Date;
  screenshotUrl?: string;
  logText?: string;
  isActive?: boolean;
  createdBy?: string;
  createdOn?: Date;
  lastModifiedBy?: string;
  lastModifiedOn?: Date;
  executionRunId?: string;
}

export interface CreateExecutionResultRequest {
  packageId?: string;
  functionalAreaId?: string;
  scenarioId?: string;
  stepId?: string;
  testDescription?: string;
  status?: string;
  executedBy?: string;
  startedAt?: Date;
  endedAt?: Date;
  screenshotUrl?: any;
  screenshot?: any; // image buffer as base64 or empty string
  logText?: string;
  clientPackageMappingId?: string;
  executionRunId?: string;
  tenantId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ExecutionResultsApiService extends MasterService<ExecutionResult> {
  
  constructor(http: HttpClient) {
    const baseUrl = `${environment.apiUrl}/execution-results`;
    super(http, baseUrl);
  }

  /**
   * Override base URL at runtime (used by Electron runner in cloud/ECS mode)
   */
  public setBaseUrl(baseUrl: string): void {
    // MasterService.baseUrl is protected; subclasses can assign
    (this as any).baseUrl = baseUrl;
  }

  /**
   * Get execution results by package ID
   */
  getByPackage(packageId: string): Observable<ExecutionResult[]> {
    return this.http.get<ExecutionResult[]>(`${this.baseUrl}/package/${packageId}`);
  }

  /**
   * Get execution results by scenario ID
   */
  getByScenario(scenarioId: string): Observable<ExecutionResult[]> {
    return this.http.get<ExecutionResult[]>(`${this.baseUrl}/scenario/${scenarioId}`);
  }

  /**
   * Get execution results by step ID
   */
  getByStep(stepId: string): Observable<ExecutionResult[]> {
    return this.http.get<ExecutionResult[]>(`${this.baseUrl}/step/${stepId}`);
  }

  /**
   * Get execution results by execution run ID
   */
  getByRunId(executionRunId: string): Observable<ExecutionResult[]> {
    return this.http.get<ExecutionResult[]>(`${this.baseUrl}/run/${executionRunId}`);
  }

  /**
   * Get execution result by ID
   */
  getExecutionResultById(id: string): Observable<ExecutionResult> {
    return this.http.get<ExecutionResult>(`${this.baseUrl}/${id}`);
  }

  /**
   * Create new execution result
   */
  createExecutionResult(data: CreateExecutionResultRequest): Observable<ExecutionResult> {
    return this.http.post<ExecutionResult>(this.baseUrl, data);
  }

  /**
   * Update existing execution result
   */
  updateExecutionResult(id: string, data: Partial<CreateExecutionResultRequest>): Observable<ExecutionResult> {
    return this.http.put<ExecutionResult>(`${this.baseUrl}/${id}`, data);
  }

  /**
   * Delete execution result (soft delete - sets isActive to false)
   */
  deleteExecutionResult(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  /**
   * Activate execution result
   */
  activateExecutionResult(id: string): Observable<ExecutionResult> {
    return this.http.put<ExecutionResult>(`${this.baseUrl}/${id}/activate`, {});
  }

  /**
   * Get execution results by status (active/inactive)
   */
  getByStatus(isActive: boolean): Observable<ExecutionResult[]> {
    return this.http.get<ExecutionResult[]>(`${this.baseUrl}/status/${isActive}`);
  }

  /**
   * Create execution result with screenshot as FormData (binary upload)
   * Reduces payload size by sending binary instead of base64
   */
  createExecutionResultWithScreenshot(data: any, screenshotBlob: Blob): Observable<ExecutionResult> {
    const formData = new FormData();
    
    // Add screenshot as binary file
    // Some backends expect 'screenshotUrl', others 'screenshot'. Send both.
    const filename = `screenshot-${data.stepId}-${Date.now()}.png`;
    formData.append('screenshotUrl', screenshotBlob, filename);
    formData.append('screenshot', screenshotBlob, filename);
    
    // Add all other fields
    if (data.packageId) formData.append('packageId', data.packageId);
    if (data.functionalAreaId) formData.append('functionalAreaId', data.functionalAreaId);
    if (data.scenarioId) formData.append('scenarioId', data.scenarioId);
    if (data.clientPackageMappingId) formData.append('clientPackageMappingId', data.clientPackageMappingId);
    if (data.stepId) formData.append('stepId', data.stepId);
    if (data.testDescription) formData.append('testDescription', data.testDescription);
    if (data.status) formData.append('status', data.status);
    if (data.startedAt) formData.append('startedAt', data.startedAt.toISOString());
    if (data.endedAt) formData.append('endedAt', data.endedAt.toISOString());
    if (data.logText) formData.append('logText', data.logText);
    if (data.executionRunId) formData.append('executionRunId', data.executionRunId);
    if (data.tenantId) formData.append('tenantId', data.tenantId);

    return this.http.post<ExecutionResult>(this.baseUrl, formData);
  }

  getExecutionImage(path: string): Observable<ExecutionResult[]> {
    return this.http.get<ExecutionResult[]>(`${this.baseUrl}/image/?path=${path}`);
  }
}